import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import axios from '../lib/axios';

export function useSeoKeywords(filters = {}) {
  return useQuery({
    queryKey: ['seo-keywords', filters],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (filters.category) params.set('category', filters.category);
      if (filters.active !== undefined) params.set('active', filters.active);
      const { data } = await axios.get(`/seo/keywords?${params}`);
      return data;
    },
  });
}

export function useCreateSeoKeyword() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload) => axios.post('/seo/keywords', payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['seo-keywords'] }),
  });
}

export function useUpdateSeoKeyword() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...payload }) => axios.patch(`/seo/keywords/${id}`, payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['seo-keywords'] }),
  });
}

export function useDeleteSeoKeyword() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id) => axios.delete(`/seo/keywords/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['seo-keywords'] }),
  });
}

export function useUpdatePosition() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, position }) => axios.post(`/seo/keywords/${id}/position`, { position }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['seo-keywords'] }),
  });
}

export function useBulkUpdatePositions() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (updates) => axios.post('/seo/keywords/bulk-update', { updates }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['seo-keywords'] }),
  });
}

export function useFetchVolumes() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const { data } = await axios.post('/seo/keywords/fetch-volumes');
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['seo-keywords'] }),
  });
}

export function useTrackInMonitor() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload = {}) => {
      const { data } = await axios.post('/seo/keywords/track-monitor', payload);
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['seo-keywords'] }),
  });
}

export function useImportKeywordsFromGsc() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload = {}) => {
      const { data } = await axios.post('/seo/keywords/import-from-gsc', payload);
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['seo-keywords'] }),
  });
}

// ── SEO Maßnahmen ──────────────────────────────────────────

export function useSeoMeasures(filters = {}) {
  return useQuery({
    queryKey: ['seo-measures', filters],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (filters.status) params.set('status', filters.status);
      if (filters.category) params.set('category', filters.category);
      const { data } = await axios.get(`/seo/measures?${params}`);
      return data;
    },
  });
}

export function useCreateSeoMeasure() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload) => axios.post('/seo/measures', payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['seo-measures'] }),
  });
}

export function useUpdateSeoMeasure() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...payload }) => axios.patch(`/seo/measures/${id}`, payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['seo-measures'] }),
  });
}

export function useDeleteSeoMeasure() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id) => axios.delete(`/seo/measures/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['seo-measures'] }),
  });
}

export function useSeedSeoMeasures() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => axios.post('/seo/measures/seed'),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['seo-measures'] }),
  });
}

export function useAnalyzeSeoOpportunities() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => axios.post('/seo/measures/analyze'),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['seo-measures'] }),
  });
}

// ── Wöchentliches Controlling ──────────────────────────────

export function useSeoWeekly() {
  return useQuery({
    queryKey: ['seo-weekly'],
    queryFn: async () => {
      const { data } = await axios.get('/seo/weekly');
      return data;
    },
  });
}
